import { useCallback, useEffect, useState } from "react";

import { supabase } from "../../../data/supabase/client";
import { logError } from "../../../utils/error";
import { useAuth } from "../../auth/AuthContext";

type UseAnalysisGroupsResponse = {
  analysisGroups: string[];
  isLoading: boolean;
  error: Error | null;
};

export const useAnalysisGroups = (): UseAnalysisGroupsResponse => {
  const { tenantId } = useAuth();
  const [analysisGroups, setAnalysisGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadAnalysisGroups = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("analysis_profile")
        .select("name")
        .eq("tenant_id", tenantId)
        .is("deleted", null)
        .order("name", { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      const groupNames = data?.map(profile => profile.name) || [];

      setAnalysisGroups(groupNames);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load analysis groups"));
      logError("Error loading analysis groups", err);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadAnalysisGroups();
  }, [loadAnalysisGroups]);

  return {
    analysisGroups,
    isLoading,
    error
  };
};
